import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Table } from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useEffect, useMemo, useState } from "react";
import { useDeleteFileMutation, useGetFilesCountQuery, useGetFilesQuery, useGetPresignedFileDownloadUrlMutation } from "../../graphql/graphql";
import { doesArrayHaveData } from "../../utils/Array.utils";
import { calculateOffset } from "../../utils/PageIndex.utils";
import { FileEmptyState } from "./FileEmptyState";
import { AutoHeightContainer } from "../../shared/Layout/AutoHeightContainer";
import { FilePreviewModal } from "./FilePreviewModal";
import { TablePagination } from "../../shared/TablePagination";
import { FileRow } from "./FileRow";
import { ManagedTable } from "../../shared/ManagedTable";
import { useTherapproveModals } from "../../shared/TherapproveOverlays";
export const FileView = ({ search = "", fileTypes }) => {
    // State
    const [page, setPage] = useState(1);
    const { fileUpload } = useTherapproveModals();
    // Effects
    useEffect(() => {
        setPage(1);
    }, [search]);
    // State
    const [filePath, setFilePath] = useState(null);
    const [fileType, setFileType] = useState(null);
    const [fileTags, setFileTags] = useState(null);
    // File Preview Modal
    const [filePreviewModalOpened, { open: filePreviewModalOpen, close: filePreviewModalClose }] = useDisclosure(false);
    // Queries
    const [{ data, fetching }] = useGetFilesQuery({
        requestPolicy: "cache-and-network",
        variables: {
            search,
            offset: calculateOffset(page),
            fileTypes: fileTypes?.length ? fileTypes : undefined
        }
    });
    const [{ data: countData }] = useGetFilesCountQuery({
        requestPolicy: "cache-and-network",
        variables: {
            search,
            fileTypes: fileTypes?.length ? fileTypes : undefined
        }
    });
    // Data
    const files = data?.files?.files ?? [];
    const count = countData?.files?.fileCount ?? 0;
    // Mutations
    const [, downloadFile] = useGetPresignedFileDownloadUrlMutation();
    const [, deleteFile] = useDeleteFileMutation();
    // Handlers
    const handleOpenModal = async (fileInfo) => {
        fileUpload.open({ fileInfo });
    };
    const handleOpenFilePreviewModal = async (fileId, fileType, tags) => {
        const { data } = await downloadFile({ fileId });
        const url = data?.files?.downloadFilePresignedUrl?.presignedUrl;
        if (url) {
            setFilePath(url);
            setFileType(fileType);
            setFileTags(tags ?? null);
            filePreviewModalOpen();
        }
    };
    const handleDownloadFile = async (fileId, name) => {
        const { data } = await downloadFile({ fileId });
        const url = data?.files?.downloadFilePresignedUrl?.presignedUrl;
        if (url) {
            // Fetch the file as a Blob
            const response = await fetch(url);
            const blob = await response.blob();
            // Create a temporary object URL for the blob
            const blobUrl = URL.createObjectURL(blob);
            // Create a hidden anchor tag to trigger the download
            const link = document.createElement("a");
            link.href = blobUrl;
            link.download = name;
            // Append to body, trigger click, then clean up
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(blobUrl);
        }
    };
    const handleNewTab = async (fileId) => {
        const { data } = await downloadFile({ fileId });
        const url = data?.files?.downloadFilePresignedUrl?.presignedUrl;
        if (url) {
            window.open(url, "_blank");
        }
    };
    const handleDeleteFile = async (fileId) => {
        await deleteFile({ fileId });
    };
    /**
     * Create the table header
     */
    const tableHead = (_jsxs(Table.Tr, { children: [_jsx(Table.Th, { style: { width: "30px" } }), _jsx(Table.Th, { children: "Name" }), _jsx(Table.Th, { children: "File Type" }), _jsx(Table.Th, { children: "Tags" }), _jsx(Table.Th, { children: "Size" }), _jsx(Table.Th, { visibleFrom: "lg", children: "Created At" }), _jsx(Table.Th, { style: { width: "50px" } })] }));
    // File Rows
    const FileRows = useMemo(() => {
        if (!doesArrayHaveData(files))
            return [];
        return files.map((file) => (_jsx(FileRow, { file: file, handlers: {
                handleOpenFilePreviewModal: handleOpenFilePreviewModal,
                handleEditFile: handleOpenModal,
                handleDownloadFile: handleDownloadFile,
                handleNewTab: handleNewTab,
                handleDelete: handleDeleteFile
            } }, file?.fileId)));
    }, [files]);
    const caption = _jsx(TablePagination, { count: count, page: page, setPage: setPage });
    return (_jsxs(AutoHeightContainer, { children: [_jsx(ManagedTable, { withBorder: true, header: tableHead, rows: FileRows, caption: caption, fetching: fetching, minWidth: 800, emptyState: _jsx(FileEmptyState, { onAddFile: () => fileUpload.open() }) }), filePath && fileType && (_jsx(FilePreviewModal, { filePath: filePath, fileType: fileType, tags: fileTags, opened: filePreviewModalOpened, close: filePreviewModalClose }))] }));
};
